import { amorphicStatic, supertypeClass, Supertype } from '@haventech/amorphic';
import { Utils } from '../../../common/js/Utils';
import { CoverpathProductClientCtx } from '../../../common/js/productService/CoverpathProductClientCtx';
import { ProductClient } from '@hit/product-client';
import { GqlTypes, ProductApiTypes } from '@hit/policy-admin-types';
import { RateClasses, RateClassObj } from '../../../common/js/static/RateClasses';
import { CoverpathWholeLifeQuote } from '../quote/coverpath/CoverpathWholeLifeQuote';
import { Agent } from '../../../common/js/customer/Agent';
import { IllustrationRequest } from './IllustrationRequest';
import { PayloadSegmentInfo, ProductServiceClient } from '../../../common/js/productService/ProductServiceClient';
import { PremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { LowFaceWholeLife, Product, RiderType } from '../../../common/js/static/Product';
import { CoverpathPolicy } from '../coverpath_subclasses/CoverpathPolicy';
import { IllustrationRequestHelpers } from './IllustrationRequestHelpers';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { remove } from 'lodash';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { HitAppCtx } from '../../../common/js/orca/HitAppCtx';
import { PolicyAdminClient as PAGQLClient } from '@hit/policy-admin-client';
import { Exchange1035FundingPurpose } from '../../js/interfaces/CoverpathTypes';
import { ProductType } from './HtmlPdfTypes';
import { FlatExtraAmendments } from '../../../common/js/static/FlatExtraAmendments';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';
import { PolicyAdminClient } from '../../../common/js/policyAdmin/PolicyAdminClient';

@supertypeClass({ toClient: false, toServer: false })
export class IllustrationService extends Supertype {
  private getQueryParameters() {
    return {
      payload: {
        policy: {
          dividendRatePercent: null,
          dividendInterestAdjustment: null,
          segments: [
            {
              startDate: null,
              riderType: null,
              benefits: [
                {
                  code: null,
                  units: null,
                  premium: null,
                  basePremium: null,
                  annualBasePremium: null,
                  fee: null,
                  annualFee: null,
                },
              ],
            },
          ],
          illustrationList: [
            {
              annualNetOutlayBegYear: null,
              paidUpAdditionsEndYear: null,
              policyYear: null,
              ageEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              cashValueOfAdditionsEndYear: null,
              contractPremiumBegYear: null,
              guaranteedCashValueEndYear: null,
              dividendPaidInCashBegYear: null,
              guaranteedContractPremiumBegYear: null,
              annualDividendEndYear: null,
              netPremiumBegYear: null,
              taxOnDistributionBegYear: null,
              totalCashValueEndYear: null,
              totalDeathBenefitEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              totalPaidUpInsuranceEndYear: null,
              costBasisBegYear: null,
              previousCostBasisBegYear: null,
              currentPaidPremium: null,
              cashValueOfAccumsEndYear: null,
            },
          ],
          mecLimit: null,
          mecYear: null,
          costIndexList: [
            {
              surrenderCostIndex: null,
              netPaymentIndex: null,
              levelDividend: null,
            },
          ],
          alirAtIssueUnits: null,
          totalAnnualTargetPremium: null,
          loanInterestRate: null,
        },
      },
    };
  }

  private getCsvQueryParameters() {
    return {
      payload: {
        policy: {
          dividendRatePercent: null,
          dividendInterestAdjustment: null,
          segments: [
            {
              startDate: null,
              riderType: null,
              benefits: [
                {
                  code: null,
                  units: null,
                  premium: null,
                  basePremium: null,
                  annualBasePremium: null,
                  fee: null,
                  annualFee: null,
                },
              ],
            },
          ],
          illustrationList: [
            {
              annualNetOutlayBegYear: null,
              paidUpAdditionsEndYear: null,
              policyYear: null,
              ageEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              cashValueOfAdditionsEndYear: null,
              contractPremiumBegYear: null,
              guaranteedCashValueEndYear: null,
              dividendPaidInCashBegYear: null,
              guaranteedContractPremiumBegYear: null,
              annualDividendEndYear: null,
              netPremiumBegYear: null,
              taxOnDistributionBegYear: null,
              totalCashValueEndYear: null,
              totalDeathBenefitEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              totalPaidUpInsuranceEndYear: null,
              costBasisBegYear: null,
              previousCostBasisBegYear: null,
              currentPaidPremium: null,
              cashValueOfAccumsEndYear: null,
              annualBasePremium: null,
              basicAnnualDividend: null,
            },
          ],
          mecLimit: null,
          mecYear: null,
          costIndexList: [
            {
              surrenderCostIndex: null,
              netPaymentIndex: null,
              levelDividend: null,
            },
          ],
          alirAtIssueUnits: null,
          totalAnnualTargetPremium: null,
        },
      },
    };
  }
  private getSupplementalQueryParameters() {
    return {
      payload: {
        solver: {
          solvedValue: null,
        },
        policy: {
          dividendRatePercent: null,
          dividendInterestAdjustment: null,
          segments: [
            {
              startDate: null,
              riderType: null,
              benefits: [
                {
                  code: null,
                  units: null,
                  premium: null,
                  basePremium: null,
                  annualBasePremium: null,
                  fee: null,
                  annualFee: null,
                },
              ],
            },
          ],
          illustrationList: [
            {
              annualNetOutlayBegYear: null,
              annualOutlayBegYear: null,
              paidUpAdditionsEndYear: null,
              policyYear: null,
              ageEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              cashValueOfAdditionsEndYear: null,
              contractPremiumBegYear: null,
              guaranteedCashValueEndYear: null,
              dividendPaidInCashBegYear: null,
              guaranteedContractPremiumBegYear: null,
              annualDividendEndYear: null,
              netPremiumBegYear: null,
              taxOnDistributionBegYear: null,
              totalCashValueEndYear: null,
              totalDeathBenefitEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              totalPaidUpInsuranceEndYear: null,
              costBasisBegYear: null,
              previousCostBasisBegYear: null,
              currentPaidPremium: null,
              cashValueOfAccumsEndYear: null,
              totalOutstandingLoanEndYear: null,
              withdrawAmount: null,
              annualLoanRequest: null,
              uncapitalizedLoanInterest: null,
            },
          ],
          mecLimit: null,
          mecYear: null,
          costIndexList: [
            {
              surrenderCostIndex: null,
              netPaymentIndex: null,
              levelDividend: null,
            },
          ],
          alirAtIssueUnits: null,
        },
      },
    };
  }

  private getWholeLifeQueryParameters() {
    return {
      payload: {
        policy: {
          dividendRatePercent: null,
          issuedDate: null,
          dividendInterestAdjustment: null,
          currentPremiums: [
            {
              paymentFrequency: null,
              basePolicyPremium: null,
              RTRPremium: null,
              GIRPremium: null,
              CIRPremium: null,
              waiverPremium: null,
              RTRWaiverPremium: null,
              GIRWaiverPremium: null,
              CIRWaiverPremium: null,
              totalInitialModalPremiumAnnualized: null,
              totalInitialModalPremium: null,
            },
          ],
          illustrationList: [
            {
              annualOutlayBegYear: null,
              annualNetOutlayBegYear: null,
              paidUpAdditionsEndYear: null,
              policyYear: null,
              ageEndYear: null,
              guaranteedDeathBenefitEndYear: null,
              cashValueOfAdditionsEndYear: null,
              contractPremiumBegYear: null,
              guaranteedCashValueEndYear: null,
              dividendPaidInCashBegYear: null,
              guaranteedContractPremiumBegYear: null,
              annualDividendEndYear: null,
              netPremiumBegYear: null,
              taxOnDistributionBegYear: null,
              totalCashValueEndYear: null,
              totalDeathBenefitEndYear: null,
              netCashValueEndYear: null,
              netDeathBenefitEndYear: null,
              totalPaidUpInsuranceEndYear: null,
              costBasisBegYear: null,
              previousCostBasisBegYear: null,
              currentPaidPremium: null,
              cashValueOfAccumsEndYear: null,
            },
          ],
          mecLimit: null,
          mecYear: null,
          costIndexList: [
            {
              surrenderCostIndex: null,
              netPaymentIndex: null,
              levelDividend: null,
            },
          ],
          alirAtIssueUnits: null,
        },
      },
    };
  }

  private getTermInforceQueryParameters() {
    return {
      payload: {
        policies: [
          {
            illustrationTable: {
              policyYear: null,
              endYearAge: null,
              proratedGuaranteedAnnualBasePremiums: null,
              proratedAnnualWaiverPremiums: null,
              proratedAnnualTotalPremiums: null,
              proratedCumulativeAnnualPremiums: null,
              endYearDeathBenefitAmount: null,
              proratedNonGuaranteedAnnualTotalPremiums: null,
              proratedNonGuaranteedCumulativeAnnualPremiums: null,
            },
            modalBasePremium: {
              monthly: null,
              quarterly: null,
              semiAnnual: null,
              annual: null,
            },
            modalWaiverPremium: {
              monthly: null,
              quarterly: null,
              semiAnnual: null,
              annual: null,
            },
            modalTotalPremium: {
              monthly: null,
              quarterly: null,
              semiAnnual: null,
              annual: null,
            },
            annualizedTotalPremium: {
              monthly: null,
              quarterly: null,
              semiAnnual: null,
              annual: null,
            },
            faceAmount: null,
            productBlock: null,
          },
        ],
      },
    };
  }

  private paymentOptionsQueryParameters(): ProductApiTypes.PremiumResponse {
    return {
      payload: {
        policies: [
          {
            segments: [
              {
                riderType: null,
                startDate: null,
                benefits: [
                  {
                    code: null,
                    units: null,
                    premium: null,
                    basePremium: null,
                    annualBasePremium: null,
                    fee: null,
                    annualFee: null,
                    tableRatingPremium: null,
                    annualTableRatingPremium: null,
                    flatExtras: [
                      {
                        name: null,
                        premium: null,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    };
  }

  private getQueryForMecDetermination() {
    return {
      payload: {
        policy: {
          mecLimit: null,
          mecYear: null,
        },
      },
    };
  }

  private getCirClaimIllustrationQueryParameters() {
    return {
      payload: {
        policy: {
          illustrationList: [
            {
              policyYear: null,
              ageEndYear: null,
              contractPremiumBegYear: null,
              cirPremium: null,
              cirBenefitPayment: null,
              cirBenefitPool: null,
              cirBenefitMonthsRemaining: null,
              totalOutstandingLoanEndYear: null,
              annualDividendEndYear: null,
              guaranteedCashValueEndYear: null,
              totalCashValueEndYear: null,
              totalDeathBenefitEndYear: null,
            },
          ],
        },
      },
    };
  }

  private async getProductServiceRequestPayload(req: IllustrationRequest) {
    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const flatExtras = { name: 'flatExtras', rate: req.flatExtra || 0 };
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.startDate,
      rateClass: rateClass,
      amount: req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: true,
    };
    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);
    const alirSegment = remove(segments, (segment) => segment.riderType === RiderType.ALIR);
    this.massageAlirSegment(alirSegment);
    let premiumType = null;
    if (req.cashValueTransfer) {
      if (req.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        premiumType = ProductApiTypes.TenThirtyFivePremiumType.PremiumAndALIR;
      } else if (req.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        premiumType = ProductApiTypes.TenThirtyFivePremiumType.ALIR;
      }
    }
    const policy = {
      version: req.productPremiumVersion,
      dividendOption: ProductApiTypes.DividendOption[req.dividendOption],
      dividendRatePercent: req.dividendRatePercent,
      dividendInterestAdjustment: req.dividendInterestAdjustment,
      startDate: req.startDate,
      taxRate: req.taxRate,
      loanRateType: ProductApiTypes.LoanRateType[req.loanRateType],
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Whole'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: segments,
      alirSchedules: alirSegment,
      totalUnits: req.faceAmount,
      paymentFrequency: ProductApiTypes.PaymentFrequency[req.paymentFrequency],
      tenThirtyFiveExchange: req.cashValueTransfer
        ? {
            premiumType: premiumType,
            cashValue: req.cashValueTransfer,
            costBasis: req.costBasis,
            startDate: Utils.offsetDate(req.startDate, req.expectedArrival, 'months'),
          }
        : null,
    };

    const payload = {
      policy: policy,
    };
    return payload;
  }

  private async getSupplementalProductServiceRequestPayload(req: IllustrationRequest) {
    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const flatExtras = { name: 'flatExtras', rate: req.flatExtra || 0 };
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.startDate,
      rateClass: rateClass,
      amount: req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: true,
    };
    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);
    const alirSegment = remove(segments, (segment) => segment.riderType === RiderType.ALIR);
    this.massageAlirSegment(alirSegment);
    const policy = {
      version: req.productPremiumVersion,
      dividendOption: ProductApiTypes.DividendOption[req.dividendOption],
      dividendRatePercent: req.dividendRatePercent,
      dividendInterestAdjustment: req.dividendInterestAdjustment,
      startDate: req.startDate,
      taxRate: req.taxRate,
      loanRateType: ProductApiTypes.LoanRateType[req.loanRateType],
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Whole'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: segments,
      alirSchedules: alirSegment,
      totalUnits: req.faceAmount,
      paymentFrequency: ProductApiTypes.PaymentFrequency[req.paymentFrequency],
    };

    const payload: ProductApiTypes.IllustrationPayloadReq = {
      policy: policy,
    };

    payload.solver = {
      problemType: ProductApiTypes.IllustrationSolverProblemType.NotOverLoanByLoanAmount,
    };

    payload.policy.distribution = {
      amount: req.disbursementAmount || 0,
      startAge: req.startAge || 0,
      endAge: req.endAge || 0,
      type:
        ProductApiTypes.DividendOption[req.dividendOption] === 'PUA'
          ? ProductApiTypes.DistributionType.WithdrawAndLoan
          : ProductApiTypes.DistributionType.Loan,
    };

    return payload;
  }

  private massageAlirSegment(alirSegment) {
    const alirProps = ['startDate', 'alirPremium', 'plannedYears', 'paymentFrequency'];
    alirSegment.forEach((segment) => {
      Object.keys(segment).forEach((prop) => {
        if (segment.hasOwnProperty(prop) && !alirProps.includes(prop)) {
          delete segment[prop];
        }
      });
    });
  }

  private async getTermInforcePaymentOptionsRequestPayload(
    req: IllustrationRequest,
    useFaceAmountDecrease: boolean = false
  ): Promise<ProductApiTypes.PremiumPayloadReq> {
    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const flatExtras = { name: 'flatExtras', rate: req.flatExtra || 0 };
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.startDate,
      rateClass: rateClass,
      amount:
        req.newFaceAmount > 0 && req.faceAmount > req.newFaceAmount && useFaceAmountDecrease
          ? req.newFaceAmount
          : req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: false,
    };

    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);
    let totalBandUnits = req.newFaceAmount > 0 && useFaceAmountDecrease ? req.newFaceAmount : req.faceAmount;
    if (req.productCode !== Product.products.CASTermECP.code && req.productCode !== Product.products.CASTerm.code) {
      totalBandUnits = req.totalBandUnits;
    }

    const policyTemplate = {
      version: req.productPremiumVersion,
      asOfDate: req.asOfDate,
      startDate: req.startDate,
      duration: req.term,
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Term'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: segments.filter((segment) => segment.riderType !== RiderType.ALIR),
      totalUnits: req.newFaceAmount > 0 && useFaceAmountDecrease ? req.newFaceAmount : req.faceAmount,
      totalBandUnits: totalBandUnits,
    };

    // This request object affects how we calculate the payment options. If order changes, check the HtmlPdfController.
    const monthlyPolicy = Object.assign({}, policyTemplate, {
      paymentFrequency: ProductApiTypes.PaymentFrequency['Monthly'],
    });
    const annualPolicy = Object.assign({}, policyTemplate, {
      paymentFrequency: ProductApiTypes.PaymentFrequency['Annual'],
    });
    return { policies: [monthlyPolicy, annualPolicy] };
  }

  private async getPaymentOptionsRequestPayload(req: IllustrationRequest): Promise<ProductApiTypes.PremiumPayloadReq> {
    const rateClass = /\d+/.test(req.rateClass) ? req.rateClass : RateClasses[req.rateClass].code;
    const hasWaiver = req.waiverUnits > 0 ? true : false;
    const flatExtras = { name: 'flatExtras', rate: req.flatExtra || 0 };
    const segmentInfo: PayloadSegmentInfo = {
      asOfDate: req.startDate,
      rateClass: rateClass,
      amount: req.faceAmount,
      riders: req.riders,
      hasWaiver: hasWaiver,
      flatExtras: flatExtras,
      includeAdbr: false,
    };

    const segments = ProductServiceClient.generateProductServicePayloadSegments(segmentInfo);

    const policyTemplate = {
      version: req.productPremiumVersion,
      asOfDate: req.asOfDate,
      startDate: req.startDate,
      duration: req.duration,
      insured: {
        gender: ProductApiTypes.Gender[req.gender === '1' ? 'M' : 'F'],
        dob: req.dob,
      },
      product: {
        type: ProductApiTypes.ProductType['Whole'],
        code: ProductApiTypes.ProductCode[req.productCode],
        state: ProductApiTypes.State[req.state],
      },
      segments: segments.filter((segment) => segment.riderType !== RiderType.ALIR),
      totalUnits: req.faceAmount,
    };

    // This request object affects how we calculate the payment options. If order changes, check the HtmlPdfController.
    const monthlyPolicy = Object.assign({}, policyTemplate, {
      paymentFrequency: ProductApiTypes.PaymentFrequency['Monthly'],
    });
    const annualPolicy = Object.assign({}, policyTemplate, {
      paymentFrequency: ProductApiTypes.PaymentFrequency['Annual'],
    });
    return { policies: [monthlyPolicy, annualPolicy] };
  }

  async getTermInforcePaymentPayloadData(req: IllustrationRequest, useFaceAmountDecrease: boolean = false) {
    const productClient = await this.initProductClient(req);
    const requestPayload: ProductApiTypes.PremiumPayloadReq = await this.getTermInforcePaymentOptionsRequestPayload(
      req,
      useFaceAmountDecrease
    );
    const query: ProductApiTypes.PremiumResponse = this.paymentOptionsQueryParameters();
    let paymentOptionsResponse;
    try {
      paymentOptionsResponse = await productClient.getPremium(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message ? err.message : 'Error response received for Whole Life Basic Illustration payment data',
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getPaymentPayloadData',
        category: UICategory.request,
        error: error,
      });
    }
    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getPaymentPayloadData',
        data: {
          size: JSON.stringify(paymentOptionsResponse).length,
        },
        category: UICategory.request,
      },
      'Response received for Whole Life Basic Illustration payment data'
    );

    return paymentOptionsResponse;
  }

  async getPaymentPayloadData(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);
    const requestPayload: ProductApiTypes.PremiumPayloadReq = await this.getPaymentOptionsRequestPayload(req);
    const query: ProductApiTypes.PremiumResponse = this.paymentOptionsQueryParameters();
    let paymentOptionsResponse;
    try {
      paymentOptionsResponse = await productClient.getPremium(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message ? err.message : 'Error response received for Whole Life Basic Illustration payment data',
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getPaymentPayloadData',
        category: UICategory.request,
        error: error,
      });
    }
    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getPaymentPayloadData',
        data: {
          size: JSON.stringify(paymentOptionsResponse).length,
        },
        category: UICategory.request,
      },
      'Response received for Whole Life Basic Illustration payment data'
    );

    return paymentOptionsResponse;
  }

  private async initProductClient(req: IllustrationRequest): Promise<ProductClient> {
    let UUID;

    if (!!req.requestId) {
      UUID = req.requestId;
    } else {
      UUID = Utils.generateUuid();
      req.requestId = UUID;
    }

    const msgCtx = {
      requestId: UUID,
      userId: UUID,
      referenceId: UUID,
      referenceType: 'Coverpath Basic Illustration Generation',
    };
    const productClientCtx = CoverpathProductClientCtx.new();
    const executionCtx = productClientCtx.newExecutionCtx(msgCtx);
    productClientCtx.exnCtx = executionCtx;
    productClientCtx.config.AUTH_TOKEN = amorphicStatic.config.nconf.get('cpApiGatewayJWTToken');
    return ProductClient.new(productClientCtx);
  }

  static createPolicyIllustrationRequest(
    agent: Agent,
    policy: CoverpathPolicy,
    isRBI: boolean = false,
    convertedFromSanderling?: boolean
  ): IllustrationRequest {
    let rateClass: string;
    const currentDate: Date = policy.effectiveDate || Utils.now();
    const asOfDate: Date = Utils.now();
    //customer.wholeLifeQuote is undefined when calling from workflow
    //hence using policy.application.quote
    //Changing productType from Term to WL using amendments
    const cpQuote = policy.application.quote;
    const taxBracket = policy.application.quote.isWholeLifeQuote()
      ? (policy.application.quote as CoverpathWholeLifeQuote).taxBracket
      : CoverpathWholeLifeQuote.defaultTaxBracket;
    const dividendInterestAdjustment = policy.application.quote.isWholeLifeQuote()
      ? (policy.application.quote as CoverpathWholeLifeQuote).dividendInterestAdjustment
      : 0;

    if (policy.finalRateClass && (convertedFromSanderling || policy.faceAmount < LowFaceWholeLife.maxFace)) {
      rateClass = RateClasses.find(policy.finalRateClass).stringCode;
    } else if (policy.isAccelerated()) {
      rateClass = RateClasses.find(policy.acceleratedRateClass).stringCode;
    } else if (policy.isTLIC()) {
      rateClass = RateClasses.find(policy.tLICRateClass).stringCode;
    } else {
      const isSmoker = policy.isSmokerByRiskFactors();
      const bmi = Utils.calculateBmi(policy.insured.weight, policy.insured.height);
      rateClass = this.adjustQuotedRateClass(RateClasses[cpQuote.healthClass], isSmoker, bmi).stringCode;
    }

    const illustrationRequest: IllustrationRequest = {
      productPremiumVersion: policy.productPremiumVersion,
      requestId: null, //This will be set with a non-null value in initProductClient
      dividendOption: policy.dividendOption,
      startDate: currentDate,
      gender: policy.insured.person.gender,
      dob: policy.insured.person.dob,
      productCode: policy.product,
      state: policy.insured.address.state,
      rateClass: rateClass,
      paymentFrequency: CoverpathUtils.makePaymentFrequencySafeForProductService(policy.paymentFrequency),
      faceAmount: policy.faceAmount,
      waiverUnits: policy.waiver ? policy.faceAmount : 0,
      flatExtra: PremiumBreakdown.getTotalFlatExtras(policy.premiumBreakdown.flatExtras),
      taxRate: Utils.round(taxBracket / 100, 2),
      loanRateType: policy.loanInterestType,
      dividendRatePercent: 1,
      dividendInterestAdjustment,
      customerPerson: IllustrationRequestHelpers.createIllustrationPerson(policy.insured.person),
      agent: IllustrationRequestHelpers.createIllustrationAgent(agent),
      duration: Product.productSpecs[policy.product].term,
      riders: IllustrationRequestHelpers.createIllustrationRiders(policy.riders),
      bankSale: !!cpQuote.bankSale,
      asOfDate: asOfDate,
    };

    const alirRider: PolicyRider = policy.getRider(RiderType.ALIR);
    const exchange1035ForAlir: Exchange1035 = alirRider && alirRider.exchange1035;
    if (alirRider && exchange1035ForAlir) {
      illustrationRequest.mecStatus = exchange1035ForAlir.isMec ? 'Yes' : 'No';
      illustrationRequest.costBasis = exchange1035ForAlir.costBasis;
      illustrationRequest.cashValueTransfer = exchange1035ForAlir.cashValueTransfer;
      illustrationRequest.expectedArrival = exchange1035ForAlir.expectedArrival;
      illustrationRequest.applyTowards = exchange1035ForAlir.applyTowards;
    } else {
      illustrationRequest.mecStatus = 'No';
      illustrationRequest.costBasis = 0;
      illustrationRequest.cashValueTransfer = 0;
      illustrationRequest.expectedArrival = 0;
      illustrationRequest.applyTowards = '';
    }

    return illustrationRequest;
  }

  static adjustQuotedRateClass(quotedRateClass: RateClassObj, isSmoker: boolean, bmi: number): RateClassObj {
    const bmiAdjustedRateClass = this.getRateClassForBmi(quotedRateClass, bmi);
    return this.getRateClassForSmoker(bmiAdjustedRateClass, isSmoker);
  }

  static getRateClassForBmi(quotedRateClass: RateClassObj, bmi: number): RateClassObj {
    const { ULTRA, SELECT, STANDARD } = RateClasses;
    const quotedCode = quotedRateClass.qCode;
    let result: RateClassObj;
    if (bmi < 31 && !(quotedCode === SELECT.qCode || quotedCode === STANDARD.qCode)) {
      result = ULTRA;
    } else if (bmi < 32 && quotedCode !== STANDARD.qCode) {
      result = SELECT;
    } else {
      result = STANDARD;
    }
    return result;
  }

  static getRateClassForSmoker(quotedRateClass: RateClassObj, isSmoker: boolean): RateClassObj {
    const { ULTRA, SELECT, STANDARD, SELECT_TOBACCO, STANDARD_TOBACCO } = RateClasses;
    const quotedCode = quotedRateClass.code;
    let result: RateClassObj;
    if (isSmoker && (quotedCode === ULTRA.code || quotedCode === SELECT.code)) {
      result = SELECT_TOBACCO;
    } else if (isSmoker && quotedCode === STANDARD.code) {
      result = STANDARD_TOBACCO;
    } else if (!isSmoker && quotedCode === SELECT_TOBACCO.code) {
      result = SELECT;
    } else if (!isSmoker && quotedCode === STANDARD_TOBACCO.code) {
      result = STANDARD;
    } else {
      result = quotedRateClass;
    }
    return result;
  }

  static policyHas1035ExchangeInfo(policy: CoverpathPolicy): boolean {
    const quoteHas1035 = policy.application.quote.has1035Exchange();
    const existing = policy.insured && policy.insured.existingPolicies;
    if (existing) {
      return existing.getTotalCashTransferValue() > 0 && quoteHas1035;
    }

    return quoteHas1035;
  }

  static createPolicyIllustrationRequestForRBI(
    agent: Agent,
    policy: CoverpathPolicy,
    convertedFromSanderling?: boolean
  ): IllustrationRequest {
    const illustrationRequestForRBI = IllustrationService.createPolicyIllustrationRequest(
      agent,
      policy,
      true,
      convertedFromSanderling
    );

    if (policy.finalRateClass || policy.tLICRateClass) {
      const rateClass = policy.finalRateClass || policy.tLICRateClass;
      //apply logic that we know we need in the context of a revised basic illustration
      illustrationRequestForRBI.rateClass = RateClasses.find(rateClass).stringCode;
    }

    this.updateIllustrationRequestWithAmendmentValues(illustrationRequestForRBI, policy);

    return illustrationRequestForRBI;
  }

  static updateIllustrationRequestWithAmendmentValues(request: IllustrationRequest, policy: CoverpathPolicy) {
    request.paymentFrequency = CoverpathUtils.makePaymentFrequencySafeForProductService(
      policy.paymentFrequencyWithPendingAmendment()
    );
    request.faceAmount = policy.faceAmountWithPendingAmendment();
    request.waiverUnits = policy.hasWaiverWithPendingAmendment() ? policy.faceAmountWithPendingAmendment() : 0;

    request.flatExtra = policy.premiumBreakdown.hasFlatExtras()
      ? PremiumBreakdown.getTotalUnitFlatExtras(FlatExtraAmendments.fetchFlatExtrasWithPendingAmendment(policy))
      : 0;

    request.productCode = policy.getProductInfoWithPendingAmendment().code;
    request.duration = policy.termWithPendingAmendment();
    request.riders = IllustrationRequestHelpers.createIllustrationRiders(policy.ridersWithPendingAmendment());
    request.startDate = policy.effectiveDateWithPendingAmendment() || Utils.now();
    request.dividendOption = policy.dividendOptionWithPendingAmendment();
    request.loanRateType = policy.loanInterestTypeWithPendingAmendment();

    // 1035 - if no alirRider but has 1035 changes, it means the 1035 has been removed
    if (policy.hasAlirRiderWithPendingAmendment() && policy.alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
      request.cashValueTransfer = policy.alirRider.alir1035ExchangeAmountWithPendingAmendment();
      request.costBasis = policy.alirRider.alir1035CostBasisWithPendingAmendment();
      request.mecStatus = policy.alirRider.alir1035IsMecWithPendingAmendment() ? 'Yes' : 'No';
      request.expectedArrival = policy.alirRider.alir1035ExpectedArrivalWithPendingAmendment();
      request.applyTowards = policy.alirRider.alir1035ApplyTowardsWithPendingAmendment();
    } else {
      request.mecStatus = 'No';
      request.costBasis = 0;
      request.cashValueTransfer = 0;
      request.expectedArrival = 0;
      request.applyTowards = '';
    }
  }

  /**
   * When using an IllustrationRequest for logging, we want to make sure there are no circular references or PII.
   */
  static cleanIllustrationRequestForLogging(original: IllustrationRequest): IllustrationRequest {
    function isPrimitive(test) {
      return test !== Object(test);
    }

    const copy = { ...original };

    Object.keys(copy).forEach((key) => {
      const value = copy[key];
      const keepIt = isPrimitive(value) || value instanceof Date;

      if (!keepIt) {
        copy[key] = null;
      }
    });

    return copy;
  }

  async getIllustrationData(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);

    const requestPayload: ProductApiTypes.IllustrationPayloadReq = await this.getProductServiceRequestPayload(req);

    const query = this.getQueryParameters();

    let illustrationResponse;

    try {
      illustrationResponse = await productClient.getIllustration(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message
          ? err.message
          : `Response received for Whole Life Basic Illustration illustrationResponse: ${JSON.stringify(
              illustrationResponse
            )}`,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getIllustrationData',
        category: UICategory.request,
        error: error,
      });
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getIllustrationData',
        data: {
          size: JSON.stringify(illustrationResponse).length,
        },
        category: UICategory.request,
      },
      `Response received for Whole Life Basic Illustration ${JSON.stringify(illustrationResponse)}`
    );

    return illustrationResponse;
  }

  async getCsvIllustrationData(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);

    const requestPayload: ProductApiTypes.IllustrationPayloadReq = await this.getProductServiceRequestPayload(req);

    const query = this.getCsvQueryParameters();

    let illustrationResponse;

    try {
      illustrationResponse = await productClient.getIllustration(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message ? err.message : `Error received getting illustration response.`,
        stack: err.stack,
        data: {
          illustrationResponse: JSON.stringify(illustrationResponse),
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getCsvIllustrationData',
        category: UICategory.request,
        error: error,
      });
      throw err;
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getCsvIllustrationData',
        data: {
          size: JSON.stringify(illustrationResponse).length,
        },
        category: UICategory.request,
      },
      `Response received for Whole Life Basic Illustration`
    );

    return illustrationResponse;
  }
  async getCirClaimIllustrationData(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);

    const requestPayload: ProductApiTypes.IllustrationPayloadReq = await this.getProductServiceRequestPayload(req);
    let predictedBenefitAge = req.riders.find((rider) => rider.type === RiderType.CIR).predictedBenefitAge;
    const currentDate = new Date();
    if (
      req.dob.getUTCMonth() < currentDate.getUTCMonth() ||
      (req.dob.getUTCMonth() === currentDate.getUTCMonth() && req.dob.getUTCDate() < currentDate.getUTCDate())
    ) {
      predictedBenefitAge = predictedBenefitAge - 1;
    }
    const claimStartDate = Utils.offsetDate(req.dob, predictedBenefitAge, 'years');
    claimStartDate.setUTCDate(currentDate.getDate());
    claimStartDate.setUTCMonth(currentDate.getMonth());
    const cirRiderIndex = requestPayload.policy.segments.findIndex((seg) => seg.riderType === 'CIR');
    requestPayload.policy.segments[cirRiderIndex].claims = [
      {
        startDate: claimStartDate,
      },
    ];

    const query = this.getCirClaimIllustrationQueryParameters();

    let cirClaimIllustrationResponse;

    try {
      cirClaimIllustrationResponse = await productClient.getCirClaimIllustration(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        message: `Response received for CIR Claim Illustration cirClaimIllustrationResponse: ${JSON.stringify(
          cirClaimIllustrationResponse
        )}`,
        name: err.name,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'cirClaimIllustrationResponse',
        category: UICategory.request,
        error: error,
      });
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'cirClaimIllustrationResponse',
        category: UICategory.request,
        data: {
          size: cirClaimIllustrationResponse ? JSON.stringify(cirClaimIllustrationResponse).length : 0,
        },
      },
      `Response received for CIR Claim Illustration`
    );

    return cirClaimIllustrationResponse;
  }

  async getSupplementalIllustrationData(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);

    const requestPayload: ProductApiTypes.IllustrationPayloadReq =
      await this.getSupplementalProductServiceRequestPayload(req);

    const query = this.getSupplementalQueryParameters();

    let illustrationResponse;

    try {
      illustrationResponse = await productClient.getIllustration(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message ? err.message : 'Response received for Whole Life Supplemental Illustration',
        stack: err.stack,
        data: {
          illustrationResponse: JSON.stringify(illustrationResponse),
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getSupplementalIllustrationData',
        category: UICategory.request,
        error: error,
      });
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getSupplementalIllustrationData',
        data: {
          size: JSON.stringify(illustrationResponse).length,
        },
        category: UICategory.request,
      },
      `Response received for Whole Life Supplemental Illustration`
    );

    return illustrationResponse;
  }

  private initPolicyAdminClient(req: IllustrationRequest) {
    let UUID;

    if (!!req.requestId) {
      UUID = req.requestId;
    } else {
      UUID = Utils.generateUuid();
      req.requestId = UUID;
    }

    const paCtx = {
      config: {
        policyAdminURL: PolicyAdminClient.getPaClientUrl(),
        AUTH_TOKEN: amorphicStatic.config.nconf.get('cpApiGatewayJWTToken'),
      },
      exnCtx: {
        msgCtx: {
          requestId: UUID,
          userId: UUID,
          referenceId: UUID,
          referenceType: 'Coverpath Inforce Illustration Generation',
        },
      },
      logger: HitAppCtx.get.logger,
    };

    return PAGQLClient.new(paCtx);
  }

  async getInforceIllustrationData(req: IllustrationRequest, inforceProductType: ProductType) {
    const policyAdminClient = this.initPolicyAdminClient(req);

    const termIllustrationRequest: GqlTypes.TermIllustrationPayloadReq = {
      policyNumber: req.policyNumber,
      isConversion: req.isPartialConversion,
    };

    const wholeLifeIllustrationRequest: GqlTypes.WholeLifeInforceIllustrationPayloadReq = {
      policyNumber: req.policyNumber,
      asOfDate: req.asOfDate,
    };

    const wholeLifeSupplementalRequest: GqlTypes.WholeLifeInforceIllustrationPayloadReq = {
      policyNumber: req.policyNumber,
      asOfDate: req.asOfDate,
      faceAmountChange: {
        newFaceAmount: req.newFaceAmount > 0 ? req.newFaceAmount : 0,
        faceIncrease: req.newFaceAmount > 0 && req.newFaceAmount > req.faceAmount,
      },
      rateClass: req.rateClassIFI,
      useGIRBenefit: req.girAmountToConvert > 0,
    };

    if (req.newFaceAmount > 0 && req.faceAmount > req.newFaceAmount && inforceProductType === ProductType.Term) {
      termIllustrationRequest.newFaceAmount = req.newFaceAmount;
    }

    const query = this.getTermInforceQueryParameters();
    const wlQuery = this.getWholeLifeQueryParameters();

    let illustrationResponse;
    try {
      if (inforceProductType === ProductType.Term) {
        illustrationResponse = await policyAdminClient.getTermInforceIllustration(termIllustrationRequest, query);
      } else if (inforceProductType === ProductType.WholeLife) {
        const illustrationReqs = [
          policyAdminClient.getWholeLifeInforceIllustration(wholeLifeIllustrationRequest, wlQuery),
        ];
        if (req.newFaceAmount > 0) {
          illustrationReqs.push(
            policyAdminClient.getWholeLifeInforceIllustration(wholeLifeSupplementalRequest, wlQuery)
          );
        }
        illustrationResponse = await Promise.all(illustrationReqs);
      }
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.stack,
        message: err.message
          ? err.message
          : `ERROR response recieved for Inforce Illustration  illustrationResponse:  ${JSON.stringify(
              illustrationResponse
            )}`,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getInforceIllustrationData',
        category: UICategory.request,
        error: error,
      });
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getInforceIllustrationData',
        data: {
          size: JSON.stringify(illustrationResponse).length,
        },
        category: UICategory.request,
      },
      `---------Response received for Inforce Illustration ------`
    );

    return illustrationResponse;
  }

  async getMecDataFromProduct(req: IllustrationRequest) {
    const productClient = await this.initProductClient(req);

    const requestPayload: ProductApiTypes.IllustrationPayloadReq = await this.getProductServiceRequestPayload(req);

    const query = this.getQueryForMecDetermination();

    let illustrationResponse;

    try {
      illustrationResponse = await productClient.getIllustration(requestPayload, query);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message
          ? err.message
          : 'Response received for Whole Life Basic Illustration while fetching mec status',
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'IllustrationService',
        function: 'getMecDataFromProduct',
        category: UICategory.request,
        error: error,
      });
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IllustrationService',
        function: 'getIllustrationData',
        data: {
          size: JSON.stringify(illustrationResponse).length,
        },
        category: UICategory.request,
      },
      'Response received for Whole Life Basic Illustration while fetching mec status'
    );

    return illustrationResponse;
  }
}
